//@refactor this component, I don't think we need this component since we already have the breadcrumbs component from the Mantine
/**
 * Breadcrumbs Component
 * Wrapper around Mantine's Breadcrumbs with custom styling
 * Last item is the current page (bold, not clickable)
 */

import {
  Anchor,
  Breadcrumbs as MantineBreadcrumbs,
  Text,
  ThemeIcon,
} from '@mantine/core';

export type BreadcrumbItem = {
  /** Display label for the breadcrumb item */
  label: string;
  /** Optional click handler - if provided, item is clickable */
  onClick?: () => void;
};

type BreadcrumbsProps = {
  /** Array of breadcrumb items - last item is treated as current page */
  items: BreadcrumbItem[];
};

/**
 * Breadcrumbs component for hierarchical navigation
 * Uses Mantine's built-in Breadcrumbs component with custom styling
 *
 * Features:
 * - Last item is current page (bold, not clickable)
 * - Previous items are clickable with hover effects
 * - Separator " > " between items
 * - Accessible with proper ARIA attributes
 *
 * @example
 * ```tsx
 * <Breadcrumbs
 *   items={[
 *     { label: "Audits", onClick: () => navigateToAudits() },
 *     { label: "SOC 2 External", onClick: () => navigateToAudit() },
 *     { label: "Control A.1.1" } // Current page (no onClick)
 *   ]}
 * />
 * ```
 */
export default function Breadcrumbs({ items }: BreadcrumbsProps) {
  // Don't render if no items
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <MantineBreadcrumbs
      separatorMargin={8}
      separator={
        <ThemeIcon size={0} fz="xs" fw={500} c="dimmed" variant="light">
          &gt;
        </ThemeIcon>
      }
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isClickable = !isLast && item.onClick;

        if (isClickable) {
          return (
            <Anchor
              key={index}
              component="button"
              type="button"
              size="xs"
              c="dimmed"
              fw={500}
              onClick={item.onClick}
              underline="hover"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              {item.label}
            </Anchor>
          );
        }

        return (
          <Text
            key={index}
            size="xs"
            c="dimmed"
            fw={isLast ? 600 : 500}
            component="span"
          >
            {item.label}
          </Text>
        );
      })}
    </MantineBreadcrumbs>
  );
}
